import React, { useMemo, useEffect, useState } from 'react';
import { statusColors } from './ConnectionData';
import { getAlpha2Code } from '../../../lib/helper/countryToAphlaCode';

const ShipmentDetails = ({ hoveredCountry, connectionData }) => {
    const [scale, setScale] = useState(1);

    // Always show all shipments
    const activeShipments = useMemo(() => {
        return connectionData || [];
    }, [connectionData]);

    // Memoize color conversion helper
    const getStatusColor = useMemo(() => (color) => `#${(color & 0xffffff).toString(16).padStart(6, '0')}`, []);

    // Detect zoom level and adjust scale
    useEffect(() => {
        const handleResize = () => {
            const zoomLevel = Math.round((window.devicePixelRatio || 1) * 100);
            
            // Adjust scale based on zoom level
            if (zoomLevel >= 150) {
                setScale(0.7); // 70% at 150% zoom
            } else if (zoomLevel >= 125) {
                setScale(0.85); // 85% at 125% zoom
            } else {
                setScale(1); // 100% at normal zoom
            }
        };

        handleResize();
        window.addEventListener('resize', handleResize);
        
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Always render the card (never return null), even if empty
    return (
        <div 
            className="absolute left-4 sm:left-6 bottom-16 sm:bottom-20 bg-white rounded-lg shadow-lg p-3 sm:p-4 w-[calc(100%-2rem)] sm:w-80 max-w-sm z-10 origin-bottom-left"
            style={{ transform: `scale(${scale})` }}
        >
            <div className="flex items-center justify-between mb-3 sm:mb-4">
                <h3 className="text-base sm:text-lg font-semibold">Your Shipments</h3>
            </div>

            <div className="space-y-2 sm:space-y-3 max-h-[300px] sm:max-h-[400px] overflow-y-auto pr-1">
                {activeShipments.length > 0 ? (
                    activeShipments.map((shipment, index) => {
                        // Pre-compute status text and color to avoid inline re-eval
                        const statusText = shipment.status === 'undefined' || shipment.status === null || shipment.status === '' ? 'Collection' : shipment.status;
                        const statusColor = getStatusColor(shipment.color || statusColors['Collection']);
                        const fromCode = getAlpha2Code(shipment.fromCountry) || (shipment.fromCountry ? shipment.fromCountry.substring(0, 2).toUpperCase() : "");
                        const toCode = getAlpha2Code(shipment.toCountry) || (shipment.toCountry ? shipment.toCountry.substring(0, 2).toUpperCase() : "");

                        return (
                            <div key={index} className="bg-gray-50 rounded-lg p-2 sm:p-3">
                                <div className="flex items-center mb-1 sm:mb-2">
                                    <div
                                        className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full mr-2 flex-shrink-0"
                                        style={{ backgroundColor: statusColor }}
                                    ></div>
                                    <span className="text-xs sm:text-sm font-medium truncate">
                                        {statusText}
                                    </span>
                                </div>

                                <div className="flex items-center">
                                    <div className="bg-gray-200 rounded-full w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center text-[10px] sm:text-xs font-medium mr-1.5 sm:mr-2 flex-shrink-0">
                                        {fromCode}
                                    </div>
                                    <div className="flex-1 h-px bg-gray-300 min-w-0"></div>
                                    <div className="px-1.5 sm:px-2 flex-shrink-0">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" className="text-gray-400 sm:w-4 sm:h-4" viewBox="0 0 16 16">
                                            <path d="M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0zM3.668 7.692a.75.75 0 0 0 1.414.06L6.4 4.528h3.2l1.318 3.224a.75.75 0 0 0 1.414-.06L9.6 3.028h2.632L11.6 1.4H4.4L3.732 3.028H6.4L3.668 7.692z" />
                                        </svg>
                                    </div>
                                    <div className="flex-1 h-px bg-gray-300 min-w-0"></div>
                                    <div className="bg-gray-200 rounded-full w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center text-[10px] sm:text-xs font-medium ml-1.5 sm:ml-2 flex-shrink-0">
                                        {toCode}
                                    </div>
                                </div>

                                <div className="flex justify-between mt-1.5 sm:mt-2 text-[10px] sm:text-xs text-gray-500 gap-2">
                                    <span className="truncate max-w-[45%]">{shipment.fromCountry}</span>
                                    <span className="truncate text-right max-w-[45%]">{shipment.toCountry}</span>
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <div className="text-center text-gray-500 py-4 sm:py-6 text-xs sm:text-sm">
                        No active shipments to display
                    </div>
                )}
            </div>
        </div>
    );
};

export default React.memo(ShipmentDetails)